/**
 * Buyer-Demand Phase 2 — admin custom-question builder.
 *
 * Question text + type (single/multi) + dynamic option list with
 * required-answer toggles. Computes `search_hash` on save (matches
 * server's stable hash so cross-session reuse aligns).
 *
 * Validation: ≥2 options, ≥1 with non-empty text, question text non-empty.
 * `required` may be empty (data-collection-only — server treats any
 * answer as satisfying the gate).
 */
import { useState } from 'react';
import { Plus, Trash2, X } from 'lucide-react';
import { customQuestionHash } from '../lib/customQuestionHash';
import type { CustomQuestion } from '../lib/adminApi';

interface Props {
  onSave: (q: CustomQuestion) => void;
  onClose: () => void;
}

interface OptionDraft {
  text: string;
  required: boolean;
}

export default function CustomQuestionBuilder({ onSave, onClose }: Props) {
  const [question, setQuestion] = useState('');
  const [type, setType] = useState<'single' | 'multi'>('single');
  const [options, setOptions] = useState<OptionDraft[]>([
    { text: '', required: false },
    { text: '', required: false },
  ]);
  const [requireAny, setRequireAny] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  function setOption(i: number, patch: Partial<OptionDraft>) {
    setOptions((prev) => prev.map((o, idx) => (idx === i ? { ...o, ...patch } : o)));
  }

  function addOption() {
    setOptions((prev) => [...prev, { text: '', required: false }]);
  }

  function removeOption(i: number) {
    setOptions((prev) => prev.filter((_, idx) => idx !== i));
  }

  async function handleSave() {
    setError(null);

    const trimmedQ = question.trim();
    if (!trimmedQ) {
      setError('Question text is required.');
      return;
    }

    const cleanOptions = options
      .map((o) => ({ ...o, text: o.text.trim() }))
      .filter((o) => o.text.length > 0);

    if (cleanOptions.length < 2) {
      setError('At least two options are required.');
      return;
    }

    const requiredValues = cleanOptions.filter((o) => o.required).map((o) => o.text);

    setSaving(true);
    const search_hash = await customQuestionHash(trimmedQ, cleanOptions.map((o) => o.text));
    setSaving(false);

    const q: CustomQuestion = {
      kind: 'custom_question',
      question: trimmedQ,
      type,
      options: cleanOptions.map((o) => o.text),
      required: requiredValues,
      search_hash,
      promoted: false,
    };

    onSave(q);
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
      <div className="bg-slate-900 rounded-xl w-full max-w-xl max-h-[85vh] flex flex-col border border-slate-700">
        <header className="flex items-center justify-between px-6 py-4 border-b border-slate-700">
          <h2 className="text-lg font-bold text-white">Add custom question</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </header>

        <div className="flex-1 overflow-auto p-6 space-y-4">
          <div>
            <label className="text-xs uppercase tracking-wide text-slate-400">Question</label>
            <textarea
              rows={2}
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder="e.g. Do you regularly buy artisanal hot sauce?"
              className="mt-1 w-full bg-slate-800 text-white rounded-lg px-3 py-2 text-sm border border-slate-700"
            />
          </div>

          <div>
            <label className="text-xs uppercase tracking-wide text-slate-400">Type</label>
            <div className="flex gap-2 mt-1">
              <TypeChip active={type === 'single'} onClick={() => setType('single')}>
                Single answer
              </TypeChip>
              <TypeChip active={type === 'multi'} onClick={() => setType('multi')}>
                Multi-select
              </TypeChip>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between">
              <label className="text-xs uppercase tracking-wide text-slate-400">Options</label>
              <button
                onClick={addOption}
                className="text-xs text-primary hover:underline inline-flex items-center gap-1"
              >
                <Plus className="w-3 h-3" /> Add option
              </button>
            </div>
            <div className="space-y-2 mt-2">
              {options.map((o, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input
                    type="text"
                    value={o.text}
                    onChange={(e) => setOption(i, { text: e.target.value })}
                    placeholder={`Option ${i + 1}`}
                    className="flex-1 bg-slate-800 text-white rounded-lg px-3 py-2 text-sm border border-slate-700"
                  />
                  <label className="flex items-center gap-1 text-xs text-slate-300">
                    <input
                      type="checkbox"
                      checked={o.required}
                      onChange={(e) => setOption(i, { required: e.target.checked })}
                    />
                    Required
                  </label>
                  {options.length > 2 && (
                    <button
                      onClick={() => removeOption(i)}
                      className="text-slate-500 hover:text-red-400"
                      aria-label="Remove option"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
            <p className="mt-2 text-xs text-slate-500">
              Mark options as <span className="text-white">Required</span> if a member must
              select them to qualify. Leave all unchecked for data-collection-only
              (any answer accepted).
            </p>
          </div>

          {type === 'multi' && (
            <label className="flex items-center gap-2 text-sm text-slate-300">
              <input
                type="checkbox"
                checked={requireAny}
                onChange={(e) => setRequireAny(e.target.checked)}
              />
              Match any required option (uncheck for "all required")
            </label>
          )}

          {error && <p className="text-sm text-red-400">{error}</p>}
        </div>

        <footer className="px-6 py-3 border-t border-slate-700 flex justify-end gap-2">
          <button onClick={onClose} className="text-sm px-4 py-2 text-slate-400 hover:text-white">
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="bg-primary text-white text-sm font-medium px-4 py-2 rounded-lg hover:bg-primary/90 disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Add question'}
          </button>
        </footer>
      </div>
    </div>
  );
}

function TypeChip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={`text-xs px-3 py-1 rounded-full transition-colors ${
        active ? 'bg-primary text-white' : 'bg-slate-800 text-slate-400 hover:text-white'
      }`}
    >
      {children}
    </button>
  );
}
